import {injectable} from "inversify";
import jsep from 'jsep';
import {arrayContains, arrayIsEqual} from "@/Utility";

/*
* The tutorial state class contains data about the tutorial, the course, the user, the customer and certain options.
* It exposes this data to the designer of the tutorial so that they can make use of it in the content itself, either
* by directly displaying the values or by checking the values using expressions as part of a slide transition.
*
* The syntax of the expressions is very much like expressions in javascript, with some changes.
*
* scenes.{scene-id}.visited: boolean
*
* -----------------------------
* Appearance
* -----------------------------
*
* appearance.primary_color: string
* appearance.accent_color: string
* appearance.supplier_logo_url: string
* appearance.supplier_logo_grayscale_url: string
*
* -----------------------------
* Customer Variables
* -----------------------------
*
* customer.name: string
* customer.support_email: string
* customer.logo_url: string
* customer.grayscale_logo_url: string
*
* -----------------------------
* Content Variables (content.*)
* -----------------------------
* These variables are declared in the tutorial manifest and the values are configurable by customers when setting up the
* course. They are intended to be used to set what content is displayed to users, e.g. which optional content segment
* to show, the e-mail address of a point of contact required in the training, etc.
*
* -----------------------------
* Question Variables (questions.*)
* -----------------------------
*
* questions.length: number
* questions.attempted: string[]
*
* questions.{id}.type: "choice"|"sequence"|"category"
* questions.{id}.text: string
* questions.{id}.min_answers: number
* questions.{id}.max_answers: number
* questions.{id}.max_attempts: number
* questions.{id}.attempted: boolean
*
* questions.{id}.items.{id}.text: string
*
* questions.{id}.attempts.length: number
* questions.{id}.attempts.latest.is_correct: boolean
* questions.{id}.attempts.latest.mark: number
* questions.{id}.attempts.latest.feedback: string
* questions.{id}.attempts.latest.given_choices: string (Multiple choice questions only)
*
* questions.{id}.attempts[n].is_correct: boolean
* questions.{id}.attempts[n].mark: number
* questions.{id}.attempts[n].feedback: string
* questions.{id}.attempts[n].given_choices: string (Multiple choice questions only)
*
* -----------------------------
* User Variables
* -----------------------------
* user.email: string
* user.first_name: string
* user.last_name: string
* user.display_name: string
*
* -----------------------------
* Options
* -----------------------------
* options.reader_mode: boolean
* options.subtitles: boolean
*
* */

export type StateValue = string|number|boolean|string[]|number[]|boolean[];

@injectable()
export class TutorialState {
    private _stateObj = {};

    public setValue(path: (string|number)[], value: any) {
        let currentVal: any = this._stateObj;
        for (let i = 0; i < path.length; i++) {
            if (i === path.length - 1)
                currentVal[path[i]] = value;
            else
                currentVal = currentVal[path[i]];
        }
    }

    public evaluate(expr: string): any {
        return evaluateStateExpression(expr, this._stateObj);
    }

    public watch(expr: string, callback: (val: any) => void) {

    }

    public toJson(): string {
        return ``;
    }

    public listVariables(path: (string|number)[]): {name: string, path: (string|number)[], value: any}[]  {
        let name = "";
        let currentVal: any = this._stateObj;
        for (let i = 0; i < path.length; i++) {
            if (typeof path[i] === 'string')
                name += i > 0 ? "." + path[i] : path[i];
            else
                name += "[" + path[i] + "]";

            currentVal = currentVal[path[i]];
        }

        let result: {name: string, path: (string|number)[], value: any}[]  = [];

        if (Array.isArray(currentVal)) {
            for (let i = 0; i < currentVal.length; i++) {
                result.push({name: name + "[" + i + "]", path: path.concat([i]), value: currentVal[i]});
            }
        }
        else {
            for (let prop in currentVal) {
                if (!currentVal.hasOwnProperty(prop))
                    continue;
                let fullName = name.length > 0 ? name + "." + prop : prop;
                result.push({name: fullName, path: path.concat([prop]), value: currentVal[prop]});
            }
        }

        return result;
    }
}

jsep.removeBinaryOp('|');
jsep.removeBinaryOp('&');
jsep.removeBinaryOp('<<');
jsep.removeBinaryOp('>>');
jsep.removeBinaryOp('>>>');
jsep.addBinaryOp('@>', 11);
jsep.addBinaryOp('??', 11);

function evaluateStateExpression(expr: string, state: any): any {
    let parseTree: jsep.Expression = jsep(expr);
    return evaluateExpression(parseTree, state);
}

function evaluateExpression(expr: jsep.Expression, state: any): any {
    if (expr.type === 'ArrayExpression') {
        return evaluateArrayExpression(<jsep.ArrayExpression> expr, state);
    }
    else if (expr.type === 'BinaryExpression') {
        return evaluateBinaryExpression(<jsep.BinaryExpression> expr, state);
    }
    else if (expr.type === 'ConditionalExpression') {
        return evaluateConditional(<jsep.ConditionalExpression> expr, state);
    }
    else if (expr.type === 'Identifier') {
        return evaluateIdentifierExpression(<jsep.Identifier> expr, state);
    }
    else if (expr.type === 'Literal') {
        return (<jsep.Literal>expr).value;
    }
    else if (expr.type === 'MemberExpression') {
        return evaluateMemberAccessExpression(<jsep.MemberExpression> expr, state);
    }
    else if (expr.type === 'UnaryExpression') {
        return evaluateUnaryExpression(<jsep.UnaryExpression> expr, state);
    }
    else {
        // not supported
    }
}

function evaluateUnaryExpression(expr: jsep.UnaryExpression, state: any) {
    switch (expr.operator) {
        case '!': return !evaluateExpression(expr.argument, state);
    }
}

function evaluateBinaryExpression(expr: jsep.BinaryExpression, state: any) {
    switch (expr.operator) {
        case '&&': return evaluateExpression(expr.left, state) && evaluateExpression(expr.right, state);
        case '||': return evaluateExpression(expr.left, state) || evaluateExpression(expr.right, state);
        case '==': return isEqual(evaluateExpression(expr.left, state), evaluateExpression(expr.right, state));
        case '!=': return !isEqual(evaluateExpression(expr.left, state), evaluateExpression(expr.right, state));
        case '<=': return evaluateExpression(expr.left, state) <= evaluateExpression(expr.right, state);
        case '>=': return evaluateExpression(expr.left, state) >= evaluateExpression(expr.right, state);
        case '<': return evaluateExpression(expr.left, state) < evaluateExpression(expr.right, state);
        case '>': return evaluateExpression(expr.left, state) > evaluateExpression(expr.right, state);
        case '+': return evaluateExpression(expr.left, state) + evaluateExpression(expr.right, state);
        case '-': return evaluateExpression(expr.left, state) - evaluateExpression(expr.right, state);
        case '*': return evaluateExpression(expr.left, state) * evaluateExpression(expr.right, state);
        case '/': return evaluateExpression(expr.left, state) / evaluateExpression(expr.right, state);
        case '^': return evaluateExpression(expr.left, state) ^ evaluateExpression(expr.right, state);
        case '%': return evaluateExpression(expr.left, state) % evaluateExpression(expr.right, state);
        case '??': return evaluateExpression(expr.left, state) ?? evaluateExpression(expr.right, state);
        case '@>': return arrayContains(evaluateExpression(expr.left, state), evaluateExpression(expr.right, state));
    }
}

function evaluateMemberAccessExpression(expr: jsep.MemberExpression, state: any) {
    let object = evaluateExpression(expr.object, state);
    if (object == null)
        return null;
    let property = evaluateExpression(expr.property, state);
    if (property == null || typeof property !== 'string')
        return null;
    if (property === '__proto__')
        return null;
    return object[property];
}

function evaluateIdentifierExpression(expr: jsep.Identifier, state: any) {
    return state[expr.name];
}

function evaluateArrayExpression(expr: jsep.ArrayExpression, state: any) {
    return expr.elements.map(e => evaluateExpression(e, state));
}

function evaluateConditional(expr: jsep.ConditionalExpression, state: any) {
    return evaluateExpression(expr.test, state)
        ? evaluateExpression(expr.consequent, state)
        : evaluateExpression(expr.alternate, state);
}

function isEqual(x: any, y: any) {
    if (Array.isArray(x)) {
        if (Array.isArray(y)) {
            return arrayIsEqual(x, y);
        }

        return false;
    }

    return x === y;
}
